import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../hooks/useAuth'
import { api } from '../lib/api'
import { Link, useNavigate } from 'react-router-dom'
import FreePreview from '../components/FreePreview'

type Tab = 'analyze' | 'history'

export default function Options() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [tab, setTab]               = useState<Tab>('analyze')
  const [ticker, setTicker]         = useState('')
  const [screenshot, setScreenshot] = useState<File | null>(null)
  const [preview, setPreview]       = useState<string | null>(null)
  const [status, setStatus]         = useState<'idle'|'loading'|'done'|'error'>('idle')
  const [result, setResult]         = useState<any>(null)
  const [errorMsg, setErrorMsg]     = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [tickerDetected, setTickerDetected] = useState(false)
  const [detectedExpiry, setDetectedExpiry] = useState<string | null>(null)

  const canUse = user?.tier === 'pro' || user?.tier === 'super'

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['options-history'],
    queryFn: () => api.options.history(20),
    enabled: tab === 'history' && canUse,
  })
  const history = Array.isArray(historyData) ? historyData : []

  const { data: detailData } = useQuery({
    queryKey: ['options-detail', expandedId],
    queryFn: () => expandedId ? api.options.detail(expandedId) : null,
    enabled: !!expandedId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.options.delete(id),
    onSuccess: () => { refetchHistory(); setExpandedId(null) },
  })

  function handleFile(file: File | null) {
    setScreenshot(file)
    if (file) {
      const r = new FileReader()
      r.onload = e => setPreview(e.target!.result as string)
      r.readAsDataURL(file)
    } else {
      setPreview(null)
    }
  }

  // Global paste handler
  useEffect(() => {
    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) { handleFile(file); break }
        }
      }
    }
    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [])

  async function analyze() {
    if (!ticker.trim() && !screenshot) return
    setStatus('loading'); setErrorMsg(''); setResult(null)
    try {
      const fd = new FormData()
      fd.append('ticker', ticker.trim().toUpperCase())
      if (screenshot) fd.append('screenshot', screenshot)
      const data = await api.options.analyze(fd)
      setResult(data)
      setStatus('done')
      // Always update ticker field from the canonical ticker returned by worker
      if (data.ticker && data.ticker !== 'UNKNOWN') {
        setTicker(data.ticker)
        // Show detected badge if it differs from what user typed
        setTickerDetected(!!data.detectedTicker && !!screenshot)
      }
      if (data.detectedExpiry) {
        setDetectedExpiry(data.detectedExpiry)
      }
      qc.invalidateQueries({ queryKey: ['options-history'] })
    } catch (e: any) {
      setErrorMsg(e.message)
      setStatus('error')
    }
  }

  const sc = (s: string) =>
    s?.toUpperCase().includes('BULL') ? 'var(--cash)' :
    s?.toUpperCase().includes('BEAR') ? 'var(--red)' : 'var(--amber)'

  if (!canUse) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Options Analysis</h1>
          <div className="eyebrow">Calls, puts, IV, max pain, gamma walls · preview mode</div>
        </div>
        <div className="analyze-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          {/* Left: show the real UI but disabled */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden', opacity: .55, pointerEvents: 'none' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)' }}>
              <span className="eyebrow">Options Chain Screenshot</span>
            </div>
            <div style={{ padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ border: '2px dashed var(--edge)', borderRadius: 4, padding: '36px 16px', textAlign: 'center' }}>
                <img src="/icons/screenshot.svg" alt="" style={{width:32,height:32,opacity:.25,marginBottom:8}}/>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.82rem', color: 'var(--muted)' }}>Drop screenshot here</div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .5, marginTop: 4 }}>Ctrl+V · any broker</div>
              </div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 7 }}>Ticker Symbol</div>
                <div style={{ display: 'flex', gap: 7 }}>
                  <div style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 13px', width: 100, fontFamily: 'Syne Mono', fontSize: '.82rem', color: 'var(--muted)' }}>AAPL</div>
                  <div style={{ flex: 1, padding: '9px', background: 'var(--cash)', opacity: .4, borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.75rem', color: 'var(--ink)', textAlign: 'center' }}>Analyze →</div>
                </div>
              </div>
            </div>
          </div>
          {/* Right: fake preview output */}
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--edge)' }}>
              <span className="eyebrow">Analysis Output</span>
            </div>
            <div style={{ padding: '18px 16px', overflowY: 'auto', maxHeight: 600 }}>
              <FreePreview type="options" onUpgrade={() => navigate('/pricing')} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* Header + tabs */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-0.02em', marginBottom: 4 }}>Options Analysis</h1>
          <div className="eyebrow">{user?.tier === 'super' ? 'Super AI' : 'Pro AI'} · screenshot for live data, or ticker-only for AI-estimated · {user?.analysesThisMonth ?? 0} analyses this month</div>
        </div>
        <div style={{ display: 'flex', border: '1px solid var(--edge)', borderRadius: 3, overflow: 'hidden' }}>
          {(['analyze', 'history'] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '7px 18px', background: tab === t ? 'var(--stamp)' : 'transparent',
              border: 'none', borderBottom: tab === t ? '2px solid var(--cash)' : '2px solid transparent',
              color: tab === t ? 'var(--cash)' : 'var(--muted)',
              fontFamily: 'Syne', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
            }}>{ t === 'analyze' ? <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/options.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> Analyze</span> : <span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/history.svg" alt="" style={{width:13,height:13,verticalAlign:'middle'}}/> History</span>}</button>
          ))}
        </div>
      </div>

      {/* ── Analyze tab ── */}
      {tab === 'analyze' && (
        <>
          <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '22px' }}>

            {/* Screenshot hero — primary method */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.9rem', color: 'var(--bill)' }}>Options Chain Screenshot</div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', padding: '3px 9px', border: '1px solid var(--cash)', color: 'var(--cash)', background: 'rgba(133,196,124,.08)', borderRadius: 2 }}>
                  ⭐ Best accuracy
                </div>
              </div>

              {/* Drop zone */}
              <div
                onClick={() => !preview && document.getElementById('opt-file-input')?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0] ?? null) }}
                style={{
                  border: `2px dashed ${preview ? 'var(--cash)' : 'var(--edge)'}`,
                  borderRadius: 3, background: preview ? 'transparent' : 'var(--paper)',
                  minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: preview ? 'default' : 'pointer', overflow: 'hidden', position: 'relative',
                  transition: 'border-color .2s',
                }}
              >
                <input id="opt-file-input" type="file" accept="image/*" onChange={e => handleFile(e.target.files?.[0] ?? null)} style={{ display: 'none' }} />
                {preview
                  ? (
                    <div style={{ position: 'relative', width: '100%' }}>
                      <img src={preview} alt="options chain" style={{ width: '100%', maxHeight: 260, objectFit: 'contain', display: 'block' }} />
                      <button
                        onClick={e => { e.stopPropagation(); handleFile(null); (document.getElementById('opt-file-input') as HTMLInputElement).value = '' }}
                        style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: 2, color: '#fff', fontFamily: 'Syne Mono', fontSize: '.6rem', padding: '4px 10px', cursor: 'pointer' }}
                      >Remove ×</button>
                    </div>
                  )
                  : (
                    <div style={{ textAlign: 'center', padding: '20px 24px' }}>
                      <img src="/icons/screenshot.svg" alt="" style={{width:36,height:36,opacity:.3,marginBottom:10}}/>
                      <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.82rem', color: 'var(--text)', marginBottom: 6 }}>Drop, tap, or paste screenshot here</div>
                      <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', lineHeight: 1.7 }}>
                        From Robinhood, Webull, Thinkorswim, Schwab, tastytrade, or any broker<br/>
                        AI reads strike prices, IV, OI, volume directly from your screenshot<br/>
                        <span style={{ color: 'var(--cash-dim)' }}>Ctrl+V / Cmd+V to paste directly from clipboard</span>
                      </div>
                      <div style={{ marginTop: 12, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', opacity: .6 }}>
                        No screenshot? AI will analyze based on ticker knowledge (less accurate)
                      </div>
                    </div>
                  )
                }
              </div>
            </div>

            {/* Ticker + submit */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div className="eyebrow" style={{ marginBottom: 7 }}>Ticker</div>
                <div style={{ position: 'relative' }}>
                  <input
                    value={ticker}
                    onChange={e => { setTicker(e.target.value.toUpperCase()); setTickerDetected(false) }}
                    onKeyDown={e => e.key === 'Enter' && analyze()}
                    placeholder={preview ? "auto-detected" : "AAPL"}
                    maxLength={10}
                    style={{
                      background: 'var(--paper)', border: `1px solid ${tickerDetected ? 'var(--cash-dim)' : 'var(--edge)'}`,
                      borderRadius: 2, padding: '10px 13px', color: 'var(--text)',
                      fontFamily: 'Syne Mono', fontSize: '.9rem', outline: 'none', width: '100%',
                    }}
                  />
                  {tickerDetected && (
                    <div style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--cash)',
                      background: 'var(--stamp)', border: '1px solid var(--cash-dim)',
                      borderRadius: 2, padding: '2px 6px', pointerEvents: 'none',
                    }}>
                      Screenshot detected
                    </div>
                  )}
                </div>
                {detectedExpiry && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <div style={{
                      fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--amber)',
                      background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.25)',
                      borderRadius: 2, padding: '2px 8px',
                    }}>
                      {detectedExpiry}
                    </div>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)' }}>expiry from screenshot</span>
                  </div>
                )}
              </div>
              <button onClick={analyze} disabled={!ticker.trim() || status === 'loading'} style={{
                padding: '10px 26px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2,
                fontFamily: 'Syne', fontWeight: 800, fontSize: '0.82rem', letterSpacing: '.06em',
                cursor: ticker.trim() && status !== 'loading' ? 'pointer' : 'not-allowed',
                opacity: !ticker.trim() || status === 'loading' ? .4 : 1, height: 42, whiteSpace: 'nowrap',
              }}>{status === 'loading' ? 'Analyzing...' : preview ? (ticker ? `Analyze ${ticker} →` : 'Analyze Screenshot →') : `Analyze ${ticker} →`}</button>
            </div>

            {/* Quick tickers */}
            <div style={{ display: 'flex', gap: 5, marginTop: 10, flexWrap: 'wrap' }}>
              {['SPY','QQQ','AAPL','NVDA','TSLA','AMZN','META','MSFT'].map(t => (
                <button key={t} onClick={() => setTicker(t)} style={{
                  padding: '3px 9px', background: ticker === t ? 'var(--stamp)' : 'transparent',
                  border: `1px solid ${ticker === t ? 'var(--cash-dim)' : 'var(--edge)'}`,
                  color: ticker === t ? 'var(--cash)' : 'var(--muted)',
                  fontFamily: 'Syne Mono', fontSize: '.6rem', borderRadius: 2, cursor: 'pointer',
                }}>{t}</button>
              ))}
            </div>
          </div>

          {/* Loading */}
          {status === 'loading' && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 180, height: 2, background: 'var(--edge)', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,var(--cash),transparent)', animation: 'sweep 1.4s ease-in-out infinite' }} />
              </div>
              <style>{`@keyframes sweep{0%{transform:translateX(-200%)}100%{transform:translateX(400%)}}`}</style>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(preview
                  ? ['Reading options chain screenshot...','Identifying strikes & expiries','Calculating put/call ratio','Analyzing IV & gamma walls','Generating strategy']
                  : ['Analyzing ticker options behavior...','Estimating positioning patterns','Assessing IV environment','Building strategy recommendation']
                ).map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--muted)', flexShrink: 0 }} />{s}
                  </div>
                ))}
              </div>
            </div>
          )}

          {status === 'error' && (
            <div style={{ background: 'rgba(224,92,92,.06)', border: '1px solid rgba(224,92,92,.25)', borderRadius: 4, padding: '16px 20px' }}>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.9rem', color: 'var(--red)', marginBottom: 6, display:'flex', alignItems:'center', gap:6 }}><img src="/icons/warning.svg" alt="" style={{width:14,height:14}}/> Analysis failed</div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--red)' }}>{errorMsg}</div>
            </div>
          )}

          {status === 'done' && result && <OptionsResult result={result} />}
        </>
      )}

      {/* ── History tab ── */}
      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {history.length === 0 && (
            <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 3, padding: 32, textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)' }}>
              No options analyses yet — run your first one
            </div>
          )}
          {history.map((h: any) => {
            const isOpen = expandedId === h.id
            const detail = isOpen ? detailData : null
            const color = sc(h.sentiment)
            return (
              <div key={h.id} style={{ background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--cash-dim)' : 'var(--edge)'}`, borderRadius: 3, overflow: 'hidden' }}>
                <div onClick={() => setExpandedId(isOpen ? null : h.id)}
                  style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: 'var(--bill)', minWidth: 64, flexShrink: 0 }}>{h.ticker}</div>
                  <div style={{ padding: '3px 10px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 700, fontSize: '.7rem', border: `1px solid ${color}`, color, background: `${color}18`, flexShrink: 0 }}>
                    {h.sentiment ?? '—'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.7rem', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.direction}</div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>{h.strategy ?? '—'}</div>
                  </div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', textAlign: 'right', flexShrink: 0, marginRight: 6 }}>
                    <div style={{ padding: '2px 6px', border: '1px solid var(--edge)', borderRadius: 2, marginBottom: 3 }}>
                      {h.dataSource === 'screenshot' ? <span style={{display:'flex',alignItems:'center',gap:3}}><img src="/icons/screenshot.svg" alt="" style={{width:10,height:10}}/> Screenshot</span> : <span style={{display:'flex',alignItems:'center',gap:3}}><img src="/icons/ai_robot.svg" alt="" style={{width:10,height:10}}/> AI Est.</span>}
                    </div>
                    <div>{new Date(h.createdAt * 1000).toLocaleDateString()}</div>
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: '.65rem', flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--edge)', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {!detail
                      ? <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', textAlign: 'center', padding: '16px 0' }}>Loading...</div>
                      : detail.error
                        ? <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--red)' }}>{detail.error}</div>
                        : <OptionsResult result={{
                            ticker: detail.ticker, dataSource: detail.dataSource,
                            sentiment: detail.sentiment, direction: detail.direction, confidence: detail.confidence,
                            maxPain: detail.maxPain, maxPainImplication: detail.maxPainImpl,
                            putCallRatio: detail.putCallRatio, putCallSignal: detail.putCallSignal,
                            ivPercentile: detail.ivPercentile, ivEnvironment: detail.ivEnvironment,
                            gammaWall: detail.gammaWall, gammaImplication: detail.gammaImpl,
                            keyCallStrikes: detail.keyCallStrikes, keyPutStrikes: detail.keyPutStrikes,
                            unusualActivity: detail.unusualActivity, expiryFocus: detail.expiryFocus,
                            strategy: detail.strategy, strategyRationale: detail.strategyRationale,
                            riskLevel: detail.riskLevel, summary: detail.summary,
                          }} compact />
                    }
                    <button
                      onClick={e => { e.stopPropagation(); if (window.confirm('Delete this analysis?')) deleteMutation.mutate(h.id) }}
                      style={{ background: 'rgba(224,92,92,.08)', border: '1px solid rgba(224,92,92,.25)', borderRadius: 2, color: 'var(--red)', fontFamily: 'Syne Mono', fontSize: '.65rem', padding: '9px', cursor: 'pointer', width: '100%' }}
                    >Delete this analysis</button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Shared result display ────────────────────────────────────────────────────
function OptionsResult({ result, compact = false }: { result: any; compact?: boolean }) {
  const sc = (s: string) =>
    s?.toUpperCase().includes('BULL') ? 'var(--cash)' :
    s?.toUpperCase().includes('BEAR') ? 'var(--red)' : 'var(--amber)'

  const bg = compact ? 'var(--paper)' : 'var(--card)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 10 : 14 }}>

      {!compact && (
        <div style={{ background: bg, border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.6rem', color: 'var(--bill)', letterSpacing: '-.02em' }}>{result.ticker}</div>
          <div style={{ padding: '7px 16px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', border: `1px solid ${sc(result.sentiment)}`, color: sc(result.sentiment), background: `${sc(result.sentiment)}18` }}>
            {result.sentiment}
          </div>
          <div style={{ padding: '7px 16px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.85rem',
            ...(result.direction?.includes('CALL') ? { border: '1px solid var(--cash)', color: 'var(--cash)', background: 'rgba(133,196,124,.1)' }
            : result.direction?.includes('PUT') ? { border: '1px solid var(--red)', color: 'var(--red)', background: 'rgba(224,92,92,.1)' }
            : { border: '1px solid var(--amber)', color: 'var(--amber)', background: 'rgba(212,168,67,.1)' }) }}>
            {result.direction}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>{result.confidence}% confidence</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', padding: '2px 7px', border: '1px solid var(--edge)', borderRadius: 2, color: 'var(--muted)' }}>
              {result.dataSource === 'screenshot' ? <span style={{display:'flex',alignItems:'center',gap:4}}><img src="/icons/screenshot.svg" alt="" style={{width:11,height:11}}/> From screenshot</span> : <span style={{display:'flex',alignItems:'center',gap:4}}><img src="/icons/ai_robot.svg" alt="" style={{width:11,height:11}}/> AI Estimated</span>}
            </div>
          </div>
        </div>
      )}

      <div className="stats-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
        {[
          { label: 'Max Pain',       val: result.maxPain,      note: result.maxPainImplication },
          { label: 'Put/Call Ratio', val: result.putCallRatio, note: result.putCallSignal },
          { label: 'IV Environment', val: result.ivPercentile, note: result.ivEnvironment },
          { label: 'Gamma Wall',     val: result.gammaWall,    note: result.gammaImplication },
        ].map(m => (
          <div key={m.label} style={{ background: bg, border: '1px solid var(--edge)', borderRadius: 3, padding: '10px 12px' }}>
            <div className="eyebrow" style={{ marginBottom: 5 }}>{m.label}</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.95rem', color: 'var(--bill)', marginBottom: 3 }}>{m.val || '—'}</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', lineHeight: 1.5 }}>{m.note}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {[
          { label: 'Key Call Strikes', val: result.keyCallStrikes, color: 'var(--cash)' },
          { label: 'Key Put Strikes',  val: result.keyPutStrikes,  color: 'var(--red)' },
          { label: 'Unusual Activity', val: result.unusualActivity, color: 'var(--amber)' },
        ].map(m => (
          <div key={m.label} style={{ background: bg, border: '1px solid var(--edge)', borderRadius: 3, padding: '10px 12px' }}>
            <div className="eyebrow" style={{ marginBottom: 7 }}>{m.label}</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.66rem', color: m.color, lineHeight: 1.7 }}>{m.val || '—'}</div>
          </div>
        ))}
      </div>

      <div style={{ background: bg, border: '1px solid var(--cash-dim)', borderRadius: 4, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <div className="eyebrow">AI Strategy</div>
          {result.expiryFocus && <div style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', padding: '2px 7px', border: '1px solid var(--cash-dim)', color: 'var(--cash)', background: 'var(--stamp)', borderRadius: 2 }}>{result.expiryFocus}</div>}
          {result.riskLevel && <div style={{ marginLeft: 'auto', fontFamily: 'Syne Mono', fontSize: '.56rem', padding: '2px 7px', border: `1px solid ${result.riskLevel === 'HIGH' ? 'rgba(224,92,92,.3)' : result.riskLevel === 'LOW' ? 'var(--cash-dim)' : 'rgba(212,168,67,.3)'}`, color: result.riskLevel === 'HIGH' ? 'var(--red)' : result.riskLevel === 'LOW' ? 'var(--cash)' : 'var(--amber)', borderRadius: 2 }}>{result.riskLevel} RISK</div>}
        </div>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.95rem', color: 'var(--bill)', marginBottom: 7 }}>{result.strategy}</div>
        <div style={{ fontFamily: 'Space Grotesk', fontSize: '.78rem', color: 'var(--text)', lineHeight: 1.7, marginBottom: 10 }}>{result.strategyRationale}</div>
        <div style={{ borderTop: '1px solid var(--edge)', paddingTop: 10, fontFamily: 'Space Grotesk', fontSize: '.75rem', color: 'var(--muted)', lineHeight: 1.7 }}>{result.summary}</div>
      </div>

      {!compact && (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--muted)', opacity: .5, textAlign: 'center', lineHeight: 1.8 }}>
          {result.dataSource === 'screenshot' ? 'Analysis based on your uploaded screenshot' : 'AI-estimated — upload a screenshot for higher accuracy'}<br/>
          Not financial advice · Options trading involves substantial risk of loss
        </div>
      )}
    </div>
  )
}
